/**
 * Claude Code Terminal — full-fidelity rendering of stream-json events matching
 * the real Claude Code CLI UX:
 *
 * - Streaming text with typing cursor (character-by-character via text_delta)
 * - Tool calls as bordered cards with output inline (framer-motion expand/collapse)
 * - Unified diffs with red/green coloring
 * - Syntax-highlighted code blocks
 * - Full markdown rendering (bold, italic, headers, lists, code, tables, clickable URLs)
 * - Running cost/token ticker in the header with estimated cost
 * - Collapsible thinking blocks with live spinner + elapsed timer
 * - Bottom status bar (model, cost, duration)
 * - Nested Agent sub-conversations
 * - Compact Read for large files
 * - Image / unknown content-type handling
 * - Completed tool cards default collapsed
 * - Live shaping (AGENT-HARNESS §15, builderStreamModel.js): folded reads, pinned Plan, now-doing, edit badges
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Loader2, TerminalSquare, ChevronDown, ChevronRight, GripHorizontal } from 'lucide-react';
import PromptCaptureLink from './PromptCaptureLink.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { tokenize, detectLang } from '../../utils/codeHighlight.js';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';
import BuilderProgressCard from './BuilderProgressCard.jsx';
import StreamBanner from './StreamBanner.jsx';
import { humanizeAgentName } from '@stepflow/ui-shared/utils/agentDisplayName.js';
import {
  deduplicateLoadedEvents, groupEvents, foldExplorations, exploreSummary, latestTodos, nowDoing, editStats,
} from './builderStreamModel.js';

// ─── Draggable resize hook ──────────────────────────────────────────────────

function useResizable({ minHeight = 80, defaultHeight = 400, maxHeight = Infinity }) {
  const [height, setHeight] = useState(defaultHeight);
  const dragging = useRef(false);
  const startY = useRef(0);
  const startH = useRef(0);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startY.current = e.clientY;
    startH.current = height;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (ev) => {
      if (!dragging.current) return;
      const delta = ev.clientY - startY.current;
      const next = Math.min(maxHeight, Math.max(minHeight, startH.current + delta));
      setHeight(next);
    };
    const onMouseUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [height, minHeight, maxHeight]);

  return { height, onMouseDown };
}

// ─── Cost estimation constants ──────────────────────────────────────────────

// Cost estimation per model family ($/1M tokens) — generated at build
// time from the canonical Python rate sheet at
// ``backend/.../stepflow_core/pricing/v1.py``. Edit that file to change
// rates; ``npm run generate:pricing`` (or any prebuild/pretest hook)
// regenerates ``ui-shared/services/generatedPricing.ts`` and the import
// below picks it up.
//
// We re-export ``MODEL_PRICING`` for the existing drift-guard test so
// the contract that "this module exposes a {haiku,sonnet,opus} shape"
// stays explicit — even though the test now mostly verifies that the
// generator ran rather than that two hand-edited tables match.
import {
  MODEL_PRICING as GENERATED_MODEL_PRICING,
  MODEL_ROWS,
  MODEL_ID_TO_FAMILY,
  CACHE_READ_MULTIPLIER,
  CACHE_CREATION_MULTIPLIER,
} from '@stepflow/ui-shared/services/generatedPricing';
export const MODEL_PRICING = GENERATED_MODEL_PRICING;

function _resolveRates(model) {
  const m = (model || '').toLowerCase();
  return m.includes('haiku') ? MODEL_PRICING.haiku
    : m.includes('opus') ? MODEL_PRICING.opus
    : MODEL_PRICING.sonnet;
}

/** Resolve the full ModelRow (with pts_input / pts_output) for a model id.
 *  Falls back to family-by-substring (sonnet default) so legacy events that
 *  don't match ``MODEL_ID_TO_FAMILY`` exactly still produce a points number. */
function _resolveModelRow(model) {
  if (!model) return MODEL_ROWS.sonnet;
  const direct = MODEL_ID_TO_FAMILY[model];
  if (direct) return MODEL_ROWS[direct];
  const m = String(model).toLowerCase();
  if (m.includes('haiku')) return MODEL_ROWS.haiku;
  if (m.includes('opus')) return MODEL_ROWS.opus;
  return MODEL_ROWS.sonnet;
}

/** Points charged for a session — mirrors backend's ``points_for_llm``:
 *  input × pts_input + output × pts_output + cache_read × pts_input × 0.10
 *  + cache_creation × pts_input × 1.25. Rounded to int (1 pt = $0.001). */
function estimatePoints(inputTokens, outputTokens, model, cacheReadTokens = 0, cacheCreationTokens = 0) {
  const r = _resolveModelRow(model);
  const total =
    inputTokens * r.pts_input
    + outputTokens * r.pts_output
    + cacheReadTokens * r.pts_input * CACHE_READ_MULTIPLIER
    + cacheCreationTokens * r.pts_input * CACHE_CREATION_MULTIPLIER;
  return Math.round(total);
}

function estimateCost(inputTokens, outputTokens, model, cacheReadTokens = 0, cacheCreationTokens = 0) {
  const r = _resolveRates(model);
  return (
    (inputTokens / 1_000_000) * r.input
    + (outputTokens / 1_000_000) * r.output
    + (cacheReadTokens / 1_000_000) * (r.input * CACHE_READ_MULTIPLIER)
    + (cacheCreationTokens / 1_000_000) * (r.input * CACHE_CREATION_MULTIPLIER)
  );
}

/** What the request would have cost without prompt caching: every cache_read
 *  and cache_creation token billed at the full input rate. The delta vs.
 *  ``estimateCost`` is the cache savings. */
function estimateUncachedCost(inputTokens, outputTokens, model, cacheReadTokens = 0, cacheCreationTokens = 0) {
  const r = _resolveRates(model);
  return (
    ((inputTokens + cacheReadTokens + cacheCreationTokens) / 1_000_000) * r.input
    + (outputTokens / 1_000_000) * r.output
  );
}

// ─── Token color mapping (matches Claude Code CLI palette) ──────────────────

const TOKEN_COLORS = {
  keyword: 'text-purple-400',
  string: 'text-green-400',
  comment: 'text-slate-400 italic',
  number: 'text-amber-400',
  function: 'text-yellow-300',
  operator: 'text-cyan-400',
  punctuation: 'text-slate-400',
  plain: 'text-slate-300',
};

function highlightCode(code, lang) {
  const detected = detectLang(lang);
  const tokens = tokenize(code, detected);
  return tokens.map((tok, i) => (
    <span key={i} className={TOKEN_COLORS[tok.type] || 'text-slate-300'}>{tok.text}</span>
  ));
}

function HighlightedCode({ code, lang }) {
  const detected = detectLang(lang);
  const tokens = useMemo(() => tokenize(code, detected), [code, detected]);
  return (
    <code>
      {tokens.map((tok, i) => (
        <span key={i} className={TOKEN_COLORS[tok.type] || 'text-slate-300'}>{tok.text}</span>
      ))}
    </code>
  );
}

// ─── Tool Card (unified block) with framer-motion animation ─────────────────

const TOOL_BODY_VARIANTS = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
};

function ToolCard({ group, isLive }) {
  const { call, result } = group;
  const tool = call?.tool || '';
  const input = call?.input || {};
  const isAgent = tool === 'Agent';

  // Completed tool cards default collapsed; live ones start expanded
  const [collapsed, setCollapsed] = useState(!isLive && !!result);

  return (
    <div className={`my-1.5 rounded-md border overflow-hidden ${
      isAgent
        ? 'ml-3 border-l-2 border-blue-800/50 border-t border-r border-b border-slate-700/80 bg-[#161b22]'
        : 'border-slate-700/80 bg-[#161b22]'
    }`}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-slate-800/40 transition-colors"
      >
        {collapsed
          ? <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" />}
        <ToolIcon tool={tool} />
        <span className="text-[11px] font-semibold text-blue-300">{tool}</span>
        <span className="text-[10px] text-slate-400 truncate flex-1">{toolSummary(tool, input)}</span>
        {tool === 'Bash' && input?.description && (
          <span className="text-[9px] text-slate-400 truncate max-w-[25%] ml-auto">{input.description}</span>
        )}
        {(() => {
          const st = editStats(tool, input);
          return st && (st.added || st.removed) ? (
            <span className="text-[9px] font-mono ml-1 shrink-0" data-testid="edit-stats">
              <span className="text-emerald-400">+{st.added}</span>
              {st.removed ? <span className="text-red-400 ml-0.5">−{st.removed}</span> : null}
            </span>
          ) : null;
        })()}
        {result && <span className="text-[9px] text-emerald-500 ml-1">✓</span>}
        {!result && <Loader2 className="h-2.5 w-2.5 animate-spin text-slate-400 ml-1" />}
      </button>
      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.div
            key="tool-body"
            initial={TOOL_BODY_VARIANTS.initial}
            animate={TOOL_BODY_VARIANTS.animate}
            exit={TOOL_BODY_VARIANTS.exit}
            transition={{ duration: 0.15 }}
            style={{ overflow: 'hidden' }}
          >
            {isAgent
              ? <AgentBody input={input} result={result} />
              : <ToolBody tool={tool} input={input} result={result} />
            }
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Exploration row (a run of reads/searches folded into one line) ─────────

function ExploreCard({ items, isLive }) {
  const running = items.some((it) => !it.group.result);
  const [open, setOpen] = useState(false);
  return (
    <div className="my-1" data-testid="explore-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-slate-800/40 transition-colors"
      >
        {open ? <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" /> : <ChevronRight className="h-3 w-3 text-slate-400 shrink-0" />}
        <span className="text-[10px] text-slate-300">{exploreSummary(items)}</span>
        {running
          ? <Loader2 className="h-2.5 w-2.5 animate-spin text-slate-400" />
          : <span className="text-[9px] text-emerald-500">✓</span>}
      </button>
      {open && (
        <div className="ml-3 border-l border-slate-700/60 pl-2">
          {items.map((it, i) => <ToolCard key={it.group.call?.id || i} group={it.group} isLive={isLive} />)}
        </div>
      )}
    </div>
  );
}

// ─── Pinned todo list (the agent's own plan — TodoWrite) ────────────────────

function TodoPanel({ todos }) {
  const [open, setOpen] = useState(true);
  const done = todos.filter((t) => t.status === 'completed').length;
  return (
    <div className="sticky top-0 z-10 -mx-3 mb-1 border-b border-slate-700/80 bg-[#0d1117]/95 px-3 py-1 backdrop-blur" data-testid="todo-panel">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-1.5 text-left">
        {open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
        <span className="text-[10px] font-semibold text-slate-200">Plan</span>
        <span className="text-[9px] text-slate-400">{done}/{todos.length} done</span>
      </button>
      {open && (
        <ul className="mt-0.5 space-y-0.5 pl-4">
          {todos.map((t, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[10px] leading-snug">
              {t.status === 'completed'
                ? <span className="text-emerald-400">✓</span>
                : t.status === 'in_progress'
                  ? <Loader2 className="mt-0.5 h-2.5 w-2.5 shrink-0 animate-spin text-amber-300" />
                  : <span className="text-slate-500">○</span>}
              <span className={t.status === 'completed' ? 'text-slate-500 line-through'
                : t.status === 'in_progress' ? 'text-amber-200' : 'text-slate-300'}>
                {t.status === 'in_progress' ? t.activeForm : t.content}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ToolIcon({ tool }) {
  const base = 'h-3 w-3 shrink-0 font-mono text-[9px] leading-3 flex items-center justify-center';
  switch (tool) {
    case 'Bash': return <span className={`${base} text-green-400`}>$</span>;
    case 'Read': return <span className={`${base} text-slate-400`}>R</span>;
    case 'Edit': return <span className={`${base} text-amber-400`}>E</span>;
    case 'Write': return <span className={`${base} text-emerald-400`}>W</span>;
    case 'Grep': case 'Glob': return <span className={`${base} text-purple-400`}>?</span>;
    case 'Agent': return <span className={`${base} text-blue-400`}>A</span>;
    default: return <span className={`${base} text-blue-400`}>⚙</span>;
  }
}

function toolSummary(tool, input) {
  if (!input || typeof input !== 'object') return '';
  switch (tool) {
    case 'Bash': return input.command ? (input.command.length > 80 ? input.command.slice(0, 80) + '…' : input.command) : '';
    case 'Read': return shortPath(input.file_path);
    case 'Edit': return shortPath(input.file_path);
    case 'Write': return shortPath(input.file_path);
    case 'Grep': return input.pattern || '';
    case 'Glob': return input.pattern || '';
    case 'Agent': return input.description || '';
    default: { const v = Object.values(input)[0]; return typeof v === 'string' ? v.slice(0, 50) : ''; }
  }
}

function shortPath(p) {
  if (!p) return '';
  const parts = p.split('/');
  return parts.length <= 3 ? p : '…/' + parts.slice(-3).join('/');
}

// ─── Tool Body ──────────────────────────────────────────────────────────────

function ToolBody({ tool, input, result }) {
  if (tool === 'Bash') return <BashBody input={input} result={result} />;
  if (tool === 'Edit') return <EditBody input={input} result={result} />;
  if (tool === 'Read') return <ReadBody input={input} result={result} />;
  if (tool === 'Write') return <WriteBody result={result} />;
  if (result?.content) return <GenericResult content={result.content} />;
  if (!result) return <PendingSpinner label="working" />;
  return null;
}

function AgentBody({ input, result }) {
  return (
    <div className="border-t border-slate-700/50">
      {input?.description && (
        <div className="px-2.5 py-1.5 text-[10px] font-semibold text-blue-200 bg-blue-950/20">
          {input.description}
        </div>
      )}
      {result?.content && (
        <div className="px-2.5 py-1.5">
          <MarkdownRenderer content={result.content} theme="dark" codeHighlighter={highlightCode} />
        </div>
      )}
      {!result && <PendingSpinner label="agent working" />}
    </div>
  );
}

function BashBody({ input, result }) {
  const cmd = input?.command || '';
  const stdout = result?.stdout || '';
  const stderr = result?.stderr || '';
  return (
    <div className="border-t border-slate-700/50 font-mono text-[10px] leading-relaxed">
      <div className="px-2.5 py-1 bg-slate-900/40">
        <span className="text-green-400/80 select-none">$ </span>
        <span className="text-slate-200 whitespace-pre-wrap break-all">{cmd}</span>
      </div>
      {(stdout || stderr) && (
        <div className="px-2.5 py-1 max-h-52 overflow-y-auto overflow-x-auto">
          {stdout && <pre className="text-slate-300 whitespace-pre-wrap break-all m-0">{trunc(stdout)}</pre>}
          {stderr && <pre className="text-red-400/80 whitespace-pre-wrap break-all m-0 mt-0.5">{trunc(stderr, 500)}</pre>}
        </div>
      )}
      {result?.interrupted && <div className="px-2.5 py-0.5 text-amber-400 text-[9px]">⚠ interrupted</div>}
      {!result && <PendingSpinner label="running" />}
    </div>
  );
}

function EditBody({ input, result }) {
  const patch = result?.patch;
  if (Array.isArray(patch) && patch.length > 0) {
    return (
      <div className="border-t border-slate-700/50 px-2.5 py-1 font-mono text-[10px] leading-relaxed max-h-64 overflow-y-auto overflow-x-auto">
        {patch.map((hunk, hi) => (
          <div key={hi}>
            <div className="text-cyan-500/70 select-none text-[9px]">@@ -{hunk.oldStart},{hunk.oldLines} +{hunk.newStart},{hunk.newLines} @@</div>
            {(() => {
              let oldLine = hunk.oldStart || 1;
              let newLine = hunk.newStart || 1;
              return (hunk.lines || []).slice(0, 40).map((line, li) => {
                const ch = line.charAt(0);
                if (ch === '-') {
                  const num = oldLine++;
                  return <div key={li} className="flex text-red-400 bg-red-950/30"><span className="select-none w-8 text-right pr-2 text-red-500/50 shrink-0">{num}</span><span className="flex-1">{line}</span></div>;
                }
                if (ch === '+') {
                  const num = newLine++;
                  return <div key={li} className="flex text-emerald-400 bg-emerald-950/30"><span className="select-none w-8 text-right pr-2 text-emerald-500/50 shrink-0">{num}</span><span className="flex-1">{line}</span></div>;
                }
                oldLine++;
                newLine++;
                return <div key={li} className="flex text-slate-400"><span className="select-none w-8 text-right pr-2 text-slate-400 shrink-0">{oldLine - 1}</span><span className="flex-1">{line}</span></div>;
              });
            })()}
            {(hunk.lines || []).length > 40 && <div className="text-slate-400 text-[9px]">…({(hunk.lines || []).length - 40} more)</div>}
          </div>
        ))}
      </div>
    );
  }
  // Fallback: show from input
  if (input?.old_string || input?.new_string) {
    return (
      <div className="border-t border-slate-700/50 px-2.5 py-1 font-mono text-[10px] leading-relaxed max-h-48 overflow-y-auto">
        {input.old_string && input.old_string.split('\n').slice(0, 10).map((l, i) => (
          <div key={`o${i}`} className="text-red-400 bg-red-950/30">-{l}</div>
        ))}
        {input.new_string && input.new_string.split('\n').slice(0, 10).map((l, i) => (
          <div key={`n${i}`} className="text-emerald-400 bg-emerald-950/30">+{l}</div>
        ))}
      </div>
    );
  }
  if (!result) return <PendingSpinner label="editing" />;
  return null;
}

function ReadBody({ input, result }) {
  const file = result?.file;
  const [showFull, setShowFull] = useState(false);

  if (!file) {
    if (!result) return <PendingSpinner label="reading" />;
    if (result.content) return <GenericResult content={result.content} />;
    return null;
  }

  const totalLines = file.totalLines || (file.content || '').split('\n').length;
  const filePath = input?.file_path || file.path || '';

  // Compact one-liner for large files
  if (totalLines > 20 && !showFull) {
    return (
      <div className="border-t border-slate-700/50 px-2.5 py-1.5 flex items-center gap-2">
        <span className="text-[10px] text-slate-400 font-mono">
          Read {totalLines} lines from {shortPath(filePath)}
        </span>
        <button
          type="button"
          onClick={() => setShowFull(true)}
          className="text-[9px] text-blue-400 hover:text-blue-300 px-1.5 py-0.5 rounded bg-slate-800/60 hover:bg-slate-800 transition-colors"
        >
          Show
        </button>
      </div>
    );
  }

  const lines = (file.content || '').split('\n').slice(0, 15);
  return (
    <div className="border-t border-slate-700/50 px-2.5 py-1 font-mono text-[10px] leading-relaxed max-h-48 overflow-y-auto overflow-x-auto">
      {lines.map((line, i) => (
        <div key={i} className="flex">
          <span className="select-none w-7 text-right pr-2 text-slate-400 shrink-0">{i + 1}</span>
          <span className="text-slate-300 whitespace-pre-wrap break-all">{line}</span>
        </div>
      ))}
      {totalLines > 15 && <div className="text-slate-400 pl-7 text-[9px]">…({totalLines - 15} more lines)</div>}
    </div>
  );
}

function WriteBody({ result }) {
  if (!result) return <PendingSpinner label="writing" />;
  return <div className="border-t border-slate-700/50 px-2.5 py-1 text-[10px] text-emerald-400/80 font-mono">✓ {result.content || 'File written'}</div>;
}

function GenericResult({ content }) {
  const lines = content.split('\n').slice(0, 20);
  return (
    <div className="border-t border-slate-700/50 px-2.5 py-1 font-mono text-[10px] text-slate-400 max-h-40 overflow-y-auto overflow-x-auto">
      {lines.map((l, i) => <div key={i} className="whitespace-pre-wrap break-all">{l}</div>)}
      {content.split('\n').length > 20 && <div className="text-slate-400">…</div>}
    </div>
  );
}

function PendingSpinner({ label }) {
  return (
    <div className="border-t border-slate-700/50 px-2.5 py-1.5 flex items-center gap-1.5">
      <Loader2 className="h-2.5 w-2.5 animate-spin text-slate-400" />
      <span className="text-[9px] text-slate-400">{label}…</span>
    </div>
  );
}

function trunc(s, max = 2500) {
  if (!s || s.length <= max) return s;
  return s.slice(0, max) + `\n… (${s.length - max} more chars)`;
}

// ─── Streaming Text Block (typing effect) ───────────────────────────────────

function StreamingTextBlock({ text, done }) {
  const clean = text.replace(/\x1b\[[0-9;?]*[a-zA-Z]|\x1b[78]|\x1b\][^\x07]*\x07?/g, '').trim();
  if (!clean) return null;
  return (
    <div className="my-1.5 relative">
      <MarkdownRenderer content={clean} theme="dark" codeHighlighter={highlightCode} />
      {!done && <span className="inline-block w-[6px] h-[14px] bg-emerald-400 animate-pulse ml-0.5 align-middle rounded-sm" />}
    </div>
  );
}

// ─── Thinking Block with spinner + elapsed timer ────────────────────────────

function ThinkingBlockLive({ startTs, text }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t0 = startTs || Date.now();
    const tick = () => setElapsed(Date.now() - t0);
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [startTs]);

  return (
    <div className="my-0.5 flex items-center gap-1.5">
      <span className="text-purple-400 text-[11px] animate-spin-slow">⟡</span>
      <Loader2 className="h-2.5 w-2.5 animate-spin text-purple-400/70" />
      <span className="text-[10px] text-purple-300/80 italic">
        Thinking… {(elapsed / 1000).toFixed(1)}s
      </span>
      {text ? (
        <span className="ml-1 truncate text-[10px] italic text-slate-500" data-testid="thinking-tail">
          {text.slice(-140)}
        </span>
      ) : null}
    </div>
  );
}

function ThinkingBlockDone({ text, startTs, endTs }) {
  const [open, setOpen] = useState(false);
  const duration = (startTs && endTs) ? ((endTs - startTs) / 1000).toFixed(1) : null;

  return (
    <div className="my-0.5">
      <button
        type="button"
        className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-300 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
        <span className="text-purple-400/70">⟡</span>
        <span className="italic">Thinking{duration ? ` (${duration}s)` : ''}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="thinking-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ overflow: 'hidden' }}
          >
            <div className="ml-4 mt-0.5 text-[10px] text-slate-400 italic whitespace-pre-wrap max-h-32 overflow-y-auto">
              {text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Image placeholder ──────────────────────────────────────────────────────

function ImagePlaceholder() {
  return (
    <div className="my-1 inline-flex items-center gap-1 rounded bg-slate-800 px-2 py-1 text-[10px] text-slate-400">
      <span>🖼</span>
      <span>[Image]</span>
    </div>
  );
}

function UnknownBlockPill({ contentType }) {
  return (
    <span className="my-1 inline-flex rounded-full bg-slate-700/60 px-2 py-0.5 text-[9px] text-slate-400 font-mono">
      {contentType}
    </span>
  );
}

// ─── Result Footer ──────────────────────────────────────────────────────────

function ResultFooter({ evt }) {
  const parts = [];
  if (evt.duration != null) parts.push(`${(evt.duration / 1000).toFixed(1)}s`);
  if (evt.cost != null) parts.push(`$${evt.cost.toFixed(4)}`);
  if (!parts.length) return null;
  return <div className="my-1 border-t border-slate-800 pt-1.5 text-[9px] text-slate-400">{parts.join(' · ')}</div>;
}

// ─── Bottom Status Bar ──────────────────────────────────────────────────────

function StatusBar({ initEvent, resultEvent, totalTokens }) {
  const parts = [];
  // L94: an avatar's cc:init may carry no model (it pins none up front) — the result names it.
  const model = initEvent?.model || resultEvent?.modelId || '';
  // Prefer the builder's session-aggregate ``cc: result`` totals when present —
  // they're de-duplicated against SDK message replays. Only fall back to summing
  // per-turn ``cc: usage`` events while the run is still live (no result yet).
  const sessionTotals = (resultEvent && resultEvent.totalInput != null) ? {
    input: Number(resultEvent.totalInput) || 0,
    output: Number(resultEvent.totalOutput) || 0,
    cacheRead: Number(resultEvent.totalCacheRead) || 0,
    cacheCreation: Number(resultEvent.totalCacheCreation) || 0,
  } : totalTokens;
  const cacheRead = sessionTotals.cacheRead || 0;
  const cacheCreation = sessionTotals.cacheCreation || 0;
  const totalIn = sessionTotals.input + cacheRead + cacheCreation;
  const hasTokens = totalIn > 0 || sessionTotals.output > 0;

  if (model) {
    // Show short model name (strip global.anthropic. prefix and version suffix)
    const short = model.replace(/^global\.anthropic\./, '').replace(/-\d{8}-v\d.*$/, '').replace(/-/g, ' ');
    parts.push(short);
  }

  // Charged cost + cache savings — ALWAYS computed from OUR rate sheet so the
  // two chips are internally consistent (charged + saved === uncached). We do
  // NOT use ``resultEvent.cost`` here: that's the SDK/Bedrock raw number on a
  // different rate sheet, and mixing it with our ``saved`` (which uses our
  // rates) makes the row fail to add up — a ~9% drift the user can't reconcile.
  // The SDK's raw cost stays in dev-tools. Points (below) are the real billing
  // unit and use this same sheet via the backend's identical formula.
  let charged = null;
  if (hasTokens) {
    charged = estimateCost(sessionTotals.input, sessionTotals.output, model, cacheRead, cacheCreation);
  }
  if (charged != null) {
    parts.push(`$${charged.toFixed(4)}`);
  }

  // Cache savings — how much this run *would* have cost without prompt caching.
  // Same rate sheet as ``charged`` above, so uncached === charged + saved.
  if (hasTokens && (cacheRead > 0 || cacheCreation > 0)) {
    const uncached = estimateUncachedCost(sessionTotals.input, sessionTotals.output, model, cacheRead, cacheCreation);
    const saved = uncached - charged;
    if (saved > 0.0001) {
      parts.push(`saved $${saved.toFixed(4)} (cache)`);
    }
  }

  if (resultEvent?.duration != null) {
    parts.push(`${(resultEvent.duration / 1000).toFixed(1)}s`);
  }

  if (hasTokens) {
    // Compact breakdown — only the cached portion is shown inline (it's
    // where the bulk of tokens live on a cache hit). Cache_creation is
    // implied by the ``saved $`` chip; the rest stays in dev-tools.
    const breakdown = cacheRead > 0 ? ` (${formatTokens(cacheRead)} cached)` : '';
    parts.push(`↓${formatTokens(totalIn)}${breakdown} ↑${formatTokens(sessionTotals.output)}`);
  }

  // Points — the user-facing billing unit (1 pt = $0.001). The customer
  // bought a points bucket; show how much of it this run consumed.
  if (hasTokens) {
    const points = estimatePoints(sessionTotals.input, sessionTotals.output, model, cacheRead, cacheCreation);
    if (points > 0) parts.push(`${points.toLocaleString()} pts`);
  }

  if (parts.length === 0) return null;

  return (
    <div className="border-t border-slate-800 px-3 py-1 text-[9px] text-slate-300 font-mono flex items-center gap-1 whitespace-nowrap">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-500">|</span>}
          <span className={p.startsWith('saved ') ? 'text-emerald-400' : ''}>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

/**
 * @param {Object} props
 * @param {Array} props.lines - array of ccEvent objects (or legacy strings)
 * @param {'live'|'completed'|'failed'} props.status
 * @param {Array} [props.builderPhases]
 */
export default function BuilderStreamTerminal({ lines, status, builderPhases, agentName, networkId, runId, hideHeader = false, fill = false }) {
  const scrollRef = useRef(null);
  const isLive = status === 'live';
  const [expanded, setExpanded] = useState(false);
  const safeLines = Array.isArray(lines) ? lines : [];
  const phases = Array.isArray(builderPhases) ? builderPhases : [];
  const { height, onMouseDown } = useResizable({ minHeight: 80, defaultHeight: 200, maxHeight: window.innerHeight * 0.7 });

  const handleHeaderDoubleClick = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const events = useMemo(() => {
    const raw = safeLines.map((item) => typeof item === 'string' ? { cc: 'text', text: item } : item);
    // For completed sessions, collapse cumulative text_delta replays into single text blocks
    if (!isLive) return deduplicateLoadedEvents(raw);
    return raw;
  }, [safeLines, isLive]);

  const { groups: grouped, initEvent, resultEvent } = useMemo(() => groupEvents(events), [events]);

  // Compute running cost from usage events
  const totalTokens = useMemo(() => {
    let input = 0, output = 0, cacheRead = 0, cacheCreation = 0;
    for (const g of grouped) {
      if (g.type === 'usage') {
        input += g.input || 0;
        output += g.output || 0;
        cacheRead += g.cacheRead || 0;
        cacheCreation += g.cacheCreation || 0;
      }
    }
    return { input, output, cacheRead, cacheCreation };
  }, [grouped]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isLive) return undefined;
    const id = window.requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    return () => window.cancelAnimationFrame(id);
  }, [grouped.length, isLive, events.length]);

  if (events.length === 0 && !isLive && phases.length === 0) return null;
  if (events.length === 0 && isLive) {
    if (phases.length > 0) return <BuilderProgressCard phases={phases} />;
    return (
      <div className="overflow-hidden rounded-lg border border-slate-800 bg-[#0d1117] p-4 shadow-md">
        <div className="flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-400" />
          <span className="text-xs text-slate-400">Preparing workspace…</span>
        </div>
      </div>
    );
  }

  const todos = latestTodos(events);
  const renderableGroups = foldExplorations(grouped.filter((g) => g.type !== 'usage'
    // the todo list is PINNED (TodoPanel) — its inline cards would only repeat it
    && !(g.type === 'tool' && g.group?.call?.tool === 'TodoWrite')).map((g) => {
    if (!isLive) {
      // Convert stale thinking_live to thinking_done for completed sessions
      if (g.type === 'thinking_live') {
        return { type: 'thinking_done', text: '', startTs: g.startTs, endTs: g.startTs };
      }
      // Mark all streaming text as done for completed sessions
      if (g.type === 'streaming_text' && !g.done) {
        return { ...g, done: true };
      }
    }
    return g;
  }));
  const doing = isLive ? nowDoing(renderableGroups, todos) : '';

  return (
    <div className={fill
      // FILL (dock host): flex-fill the parent — the DOCK owns the drag/height, so the terminal
      // must EXPAND/CONTRACT with its pane (no fixed inner height, no own grip, status bar pinned).
      ? 'flex h-full min-h-0 flex-col overflow-hidden bg-[#0d1117]'
      : hideHeader
        ? 'overflow-hidden bg-[#0d1117]'
        : 'overflow-hidden rounded-lg border border-slate-800 bg-[#0d1117] shadow-md'}>
      {/* Header — double-click to expand/collapse. Suppressed (hideHeader) when this terminal is
          hosted INSIDE the tree's shared reader: the panel's SINGLE header already shows the agent
          name + Context, so a second dark header here would be the "two lines / embedded window"
          look. Same lift-and-shift the worker does via AgentResponsePanel.hideChrome. */}
      {!hideHeader ? (
      <div className="flex items-center gap-2 border-b border-slate-700/80 bg-[#161b22] px-3 py-1.5 cursor-pointer select-none" onDoubleClick={handleHeaderDoubleClick}>
        <TerminalSquare className={`h-3.5 w-3.5 ${isLive ? 'animate-pulse text-emerald-400' : 'text-slate-400'}`} />
        <span className="text-[11px] font-semibold text-slate-200" title={initEvent?.agentId || agentName || 'Builder'}>{humanizeAgentName(initEvent?.agentId || agentName || 'Builder')}</span>
        {isLive ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-950/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            live
          </span>
        ) : (
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
            status === 'failed' ? 'bg-red-950/70 text-red-300' : 'bg-slate-700/60 text-slate-300'
          }`}>
            {status === 'failed' ? 'error' : 'done'}
          </span>
        )}
        {/* "Resumed from previous session" indicator. Set by stream_runner
            when the per-(network, pool) ``~/.claude`` cache hit AND the
            smart-resume classifier picked this session as a continuation
            of the new prompt. Phase 1.5 adds ``label`` (the human-readable
            task name) and ``confidence`` (the classifier's certainty) so
            the chip reads "RESUMED · auth refactor (87%)" instead of an
            opaque session id. Full session id stays in the tooltip for
            support / log correlation. */}
        {initEvent?.resumed ? (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-violet-950/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-300"
            title={[
              initEvent?.label ? `Task: ${initEvent.label}` : null,
              initEvent?.sessionId ? `Session: ${initEvent.sessionId}` : null,
              typeof initEvent?.confidence === 'number'
                ? `Classifier confidence: ${Math.round(initEvent.confidence * 100)}%`
                : null,
            ].filter(Boolean).join('\n') || 'Resumed from previous session'}
          >
            <span aria-hidden>🧠</span>
            resumed
            {initEvent?.label ? (
              <span className="font-normal normal-case text-violet-200">· {initEvent.label}</span>
            ) : null}
            {typeof initEvent?.confidence === 'number' && initEvent.confidence > 0 ? (
              <span className="font-normal text-violet-400/80">
                ({Math.round(initEvent.confidence * 100)}%)
              </span>
            ) : null}
          </span>
        ) : initEvent?.label ? (
          /* Fresh start, but the classifier proposed a label for the new
             task — surface it so users see the agent's read of what it's
             doing. Different colored pill so it doesn't look like resume. */
          <span
            className="inline-flex items-center gap-1 rounded-full bg-sky-950/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-sky-300"
            title={`New task: ${initEvent.label}`}
          >
            <span aria-hidden>✨</span>
            <span className="font-normal normal-case text-sky-200">{initEvent.label}</span>
          </span>
        ) : null}
        {/*
          Token/cost ticker is rendered ONLY in the bottom StatusBar.
          The previous header ticker showed ~$0.00 throughout live runs
          because Claude SDK ``usage`` events only arrive at the end of
          each turn — leading to a misleading "always zero" display. The
          bottom bar reads the same totals plus the SDK's authoritative
          ``result.cost`` when present, so it's accurate AND less noisy.
        */}
        {/* "Context" — the StepFlow additions to this builder agent's prompt.
            Self-gates (renders nothing without networkId/agent/runId). Dark
            header, so override the link's light hover styles. */}
        <span className="ml-auto" onDoubleClick={(e) => e.stopPropagation()}>
          <PromptCaptureLink
            networkId={networkId}
            source="builder"
            agent={initEvent?.agentId || agentName || 'Builder'}
            run={runId}
          />
        </span>
      </div>
      ) : null}
      {/* Event stream. FILL host → flex-fill the pane (parent owns the height, so it grows/shrinks
          with the dock drag). Standalone → the local drag-handle height (or expanded = auto). */}
      <div
        ref={scrollRef}
        style={fill || expanded ? {} : { height: `${height}px` }}
        className={`px-3 py-2 space-y-0.5 overflow-x-hidden ${
          fill ? 'min-h-0 flex-1 overflow-y-auto' : expanded ? '' : 'overflow-y-auto'}`}
        role="log"
        aria-live={isLive ? 'polite' : 'off'}
      >
        {todos && todos.length ? <TodoPanel todos={todos} /> : null}
        {renderableGroups.map((item, idx) => {
          if (item.type === 'explore') return <ExploreCard key={idx} items={item.items} isLive={isLive} />;
          if (item.type === 'tool') return <ToolCard key={idx} group={item.group} isLive={isLive} />;
          if (item.type === 'streaming_text') return <StreamingTextBlock key={idx} text={item.text} done={item.done} />;
          if (item.type === 'thinking_live') return <ThinkingBlockLive key={idx} startTs={item.startTs} text={item.text} />;
          if (item.type === 'thinking_done') return <ThinkingBlockDone key={idx} text={item.text} startTs={item.startTs} endTs={item.endTs} />;
          if (item.type === 'image') return <ImagePlaceholder key={idx} />;
          if (item.type === 'unknown_block') return <UnknownBlockPill key={idx} contentType={item.contentType} />;
          if (item.type === 'result') return <ResultFooter key={idx} evt={item.evt} />;
          if (item.type === 'banner') return <StreamBanner key={idx} evt={item.evt} />;
          const { evt } = item;
          if (!evt) return null;
          if (evt.cc === 'thinking') return <ThinkingBlockDone key={idx} text={evt.text} startTs={null} endTs={null} />;
          if (evt.cc === 'text') return <StreamingTextBlock key={idx} text={evt.text} done={true} />;
          if (evt.cc === 'result') return <ResultFooter key={idx} evt={evt} />;
          return null;
        })}
        {isLive && (
          <div className="flex items-center gap-1.5 pt-1">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[9px] text-slate-400 truncate" data-testid="now-doing">{doing}…</span>
          </div>
        )}
      </div>
      {/* Drag handle — the terminal's OWN resizer. Hidden in FILL mode: the dock owns the height,
          so a second grip here is redundant and confusing (the user's ask). Standalone keeps it. */}
      {!fill ? (
        <div
          onMouseDown={onMouseDown}
          className="flex items-center justify-center h-2 cursor-row-resize border-t border-slate-700/60 bg-[#161b22] hover:bg-slate-700/40 transition-colors group"
        >
          <GripHorizontal className="h-2.5 w-3.5 text-slate-400 group-hover:text-slate-400" />
        </div>
      ) : null}
      {/* Bottom status bar — pinned (shrink-0) so in FILL mode it stays at the pane's bottom while
          the stream above scrolls, instead of floating with the content. */}
      <div className="shrink-0">
        <StatusBar initEvent={initEvent} resultEvent={resultEvent} totalTokens={totalTokens} />
      </div>
    </div>
  );
}

function formatTokens(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
}
